import { useCallback } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { UserNotification } from '../types/gotypes'

interface ListFeedResponse {
  notifications: UserNotification[]
  count: number
  unread_count: number
}

// useNotifications drives the header bell (#964 v1): unread count + the
// dropdown list, both polled via TanStack Query so a notify from another
// tab/user shows up "the next time you load the bell" — the real-time
// definition the approved design settled on (no push, no digest).
export const useNotifications = ({ customer }: { customer: string }) => {
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const feedQueryKey = ['notifications-feed', { customer }]

  const feedQuery = useQuery({
    queryKey: feedQueryKey,
    enabled: Boolean(customer),
    // 60s poll — cheap, and frequent enough that "next time you load the
    // bell" in practice means "within a minute" for anyone with the app open.
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await axios.get<ListFeedResponse>(
        `${API_BASE_URL}/notifications/feed`,
        { params: { customer, limit: 25 } }
      )
      return res.data
    },
  })

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.post(`${API_BASE_URL}/notifications/${id}/read`, null, { params: { customer } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API_BASE_URL}/notifications/read-all`, null, { params: { customer } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Re-index from a job_reindex_needed notice (#2013): starts the job's
  // build (or finds one already running), then marks the notice read. The
  // notice resolves itself once the build completes.
  const reindexMutation = useMutation({
    mutationFn: async (n: UserNotification) => {
      const job = n.payload?.job || ''
      const res = await axios.post<{ alreadyRunning?: boolean }>(
        `${API_BASE_URL}/job/${encodeURIComponent(n.customer)}/${encodeURIComponent(job)}/index`
      )
      if (!n.read_at) {
        await axios.post(`${API_BASE_URL}/notifications/${n.id}/read`, null, { params: { customer } })
      }
      return { job, alreadyRunning: !!res.data?.alreadyRunning }
    },
    onSuccess: ({ job, alreadyRunning }) => {
      if (alreadyRunning) snackbar.info(`A re-index is already running for ${job}`)
      else snackbar.success(`Re-index started for ${job}`)
      queryClient.invalidateQueries({ queryKey: feedQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const markRead = useCallback((id: string) => markReadMutation.mutate(id), [markReadMutation])
  const markAllRead = useCallback(() => markAllReadMutation.mutate(), [markAllReadMutation])
  const handleReindex = useCallback((n: UserNotification) => reindexMutation.mutate(n), [reindexMutation])

  return {
    notifications: (feedQuery.data?.notifications || []) as UserNotification[],
    unreadCount: feedQuery.data?.unread_count || 0,
    isLoading: feedQuery.isLoading,
    isError: feedQuery.isError,
    refetch: feedQuery.refetch,

    markRead,
    markAllRead,
    handleReindex,
  }
}

export default useNotifications
